import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SiteContentProvider } from "./contexts/SiteContentContext";
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import MagmaCursor from "./components/brand/MagmaCursor";

import Home from "./pages/Home";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import TableReservation from "./pages/TableReservation";
import PrivateEvent from "./pages/PrivateEvent";
import MyStory from "./pages/MyStory";
import ProductDetail from "./pages/ProductDetail";
import CocktailMenu from "./pages/CocktailMenu";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Account from "./pages/Account";
import Admin from "./pages/Admin";
import MenuEditor from "./pages/MenuEditor";
import IngredientsAdmin from "./pages/IngredientsAdmin";
import ContentEditor from "./pages/ContentEditor";
import POS from "./pages/POS";
import POSBar from "./pages/POSBar";
import POSService from "./pages/POSService";
import POSAuthGate from "./pages/POSAuthGate";
import TableMenu, { TableSuccess } from "./pages/TableMenu";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import GiftCards from "./pages/GiftCards";
import GiftCardSuccess from "./pages/GiftCardSuccess";
import Shop from "./pages/Shop";
import ShopSuccess from "./pages/ShopSuccess";
import MentionsLegales from "./pages/MentionsLegales";
import CGV from "./pages/CGV";
import Confidentialite from "./pages/Confidentialite";
import CookieConsent from "./components/CookieConsent";
import { useLocation } from "react-router-dom";
import { initPostHogIfConsented } from "./lib/analytics";

function Layout({ children }) {
  const { pathname } = useLocation();
  // Tablet-mode: on /pos & /pos/bar we hide the customer nav + footer so the
  // staff view fills the entire screen. The PIN gate + POS UI already own
  // the whole viewport (~calc(100vh - 4rem) → 100vh once chrome is gone).
  const isPosSurface = pathname.startsWith("/pos") || pathname.startsWith("/table/");
  return (
    <div className="App flex flex-col min-h-screen subtle-grain">
      {!isPosSurface && <Header />}
      <main className="flex-1">{children}</main>
      {!isPosSurface && <Footer />}
      <CookieConsent />
    </div>
  );
}

export default function App() {
  // Boot-time: initialise PostHog only if the visitor previously granted
  // consent. Skipped on first visit (banner will handle it).
  if (typeof window !== "undefined") initPostHogIfConsented();
  return (
    <AuthProvider>
      <SiteContentProvider>
        <BrowserRouter>
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/evenements" element={<Events />} />
              <Route path="/evenements/:id" element={<EventDetail />} />
              <Route path="/reservation-table" element={<TableReservation />} />
              <Route path="/reserver" element={<TableReservation />} />
              <Route path="/reserver-table" element={<TableReservation />} />
              <Route path="/evenement-prive" element={<PrivateEvent />} />
              <Route path="/bar-mobile" element={<PrivateEvent />} />
              <Route path="/mon-histoire" element={<MyStory />} />
              <Route path="/carte" element={<CocktailMenu />} />
              <Route path="/boutique" element={<Shop />} />
              <Route path="/boutique/succes" element={<ShopSuccess />} />
              <Route path="/boutique/:slug" element={<ProductDetail />} />
              <Route path="/cartes-cadeaux" element={<GiftCards />} />
              <Route path="/cadeaux" element={<GiftCards />} />
              <Route path="/cadeau-paiement-succes" element={<GiftCardSuccess />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/cgv" element={<CGV />} />
              <Route path="/confidentialite" element={<Confidentialite />} />
              <Route path="/connexion" element={<Login />} />
              <Route path="/inscription" element={<Register />} />
              <Route path="/compte" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
              <Route path="/admin/carte" element={<ProtectedRoute adminOnly><MenuEditor /></ProtectedRoute>} />
              <Route path="/admin/ingredients" element={<ProtectedRoute adminOnly><IngredientsAdmin /></ProtectedRoute>} />
              <Route path="/admin/contenu" element={<ProtectedRoute adminOnly><ContentEditor /></ProtectedRoute>} />
              <Route path="/pos" element={<POSAuthGate><POS /></POSAuthGate>} />
              <Route path="/pos/bar" element={<POSAuthGate><POSBar /></POSAuthGate>} />
              <Route path="/pos/service" element={<POSAuthGate><POSService /></POSAuthGate>} />
              <Route path="/table/:num" element={<TableMenu />} />
              <Route path="/table/:num/merci" element={<TableSuccess />} />
              <Route path="/paiement-succes" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
              <Route path="/paiement-annule" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
          <Toaster richColors position="top-right" />
          <MagmaCursor />
        </BrowserRouter>
      </SiteContentProvider>
    </AuthProvider>
  );
}
